import { Component, computed, inject, signal, Output, EventEmitter, effect } from '@angular/core';
import { NgClass } from '@angular/common';
import { toSignal, toObservable } from '@angular/core/rxjs-interop';
import { Router, NavigationEnd } from '@angular/router';
import { filter, map, forkJoin, of, catchError, debounceTime, switchMap } from 'rxjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SelectModule } from 'primeng/select';
import { FormsModule } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { AvatarModule } from 'primeng/avatar';
import { MenuModule } from 'primeng/menu';
import { MenuItem } from 'primeng/api';
import { AuthService } from '../../../../core/auth/auth.service';
import { NotificationCenter } from '../../notification-center/notification-center';
import { ApiService } from '../../../../core/services/api.service';
import { environment } from '../../../../environments/environment';

interface LangOption {
  code: string;
  label: string;
  flag: string;
  dir: 'ltr' | 'rtl';
}

// Map route segment -> i18n key
const ROUTE_TITLE_MAP: Record<string, string> = {
  'dashboard':        'nav.dashboard',
  'users':            'nav.users',
  'drivers':          'nav.drivers',
  'trips':            'nav.trips',
  'bookings':         'nav.bookings',
  'service-requests': 'nav.serviceRequests',
  'activity':         'nav.activity',
  'ratings':          'nav.ratings',
  'reviews':          'nav.reviews',
  'compliance':       'nav.compliance',
  'tracking':         'nav.tracking',
  'dispatch':         'nav.dispatch',
  'geofencing':       'nav.geofencing',
  'payments':         'nav.payments',
  'subscriptions':    'nav.subscriptions',
  'fees':             'nav.fees',
  'categories':       'nav.categories',
  'locations':        'nav.locations',
  'geo':              'nav.geo',
  'promos':           'nav.promos',
  'employers':        'nav.employers',
  'team':             'nav.team',
  'gatekeeper':       'nav.gatekeeper',
  'airports':         'nav.airports',
  'notifications':    'nav.notifications',
  'settings':         'nav.settings',
};

type SearchCategory = 'user' | 'driver' | 'trip' | 'booking' | 'payment' | 'employer' | 'promo' | 'service-request' | 'subscription';

interface SearchResult {
  type: SearchCategory;
  id: number;
  label: string;
  sub: string;
  route: string;
  icon: string;
}

interface SearchSource {
  apiPath: string;
  type: SearchCategory;
  icon: string;
  toResult: (item: any) => { label: string; sub: string; route: string };
}

const SEARCH_SOURCES: SearchSource[] = [
  { apiPath: '/passengers', type: 'user', icon: 'pi pi-user',
    toResult: (u) => ({ label: `${u.firstName} ${u.lastName}`, sub: u.email, route: `/passengers/${u.id}` }) },
  { apiPath: '/drivers', type: 'driver', icon: 'pi pi-car',
    toResult: (d) => ({ label: d.name, sub: d.email, route: `/drivers/${d.id}` }) },
  { apiPath: '/trips', type: 'trip', icon: 'pi pi-map',
    toResult: (t) => ({ label: `Trip #${t.id}`, sub: `${t.passengerName} → ${t.driverName}`, route: `/trips/${t.id}` }) },
  { apiPath: '/payments', type: 'payment', icon: 'pi pi-credit-card',
    toResult: (p) => ({ label: `Payment #${p.id}`, sub: `$${p.amount} — ${p.status}`, route: `/payments/${p.id}` }) },
  { apiPath: '/employees', type: 'employer', icon: 'pi pi-building',
    toResult: (e) => ({ label: e.name, sub: e.email ?? e.status, route: `/employees/${e.id}` }) },
  { apiPath: '/promos', type: 'promo', icon: 'pi pi-tag',
    toResult: (p) => ({ label: p.code, sub: `${p.discountType} ${p.discountValue}`, route: `/promos/${p.id}/edit` }) },
];

@Component({
  selector: 'app-topbar',
  imports: [TranslateModule, SelectModule, FormsModule, InputTextModule, NotificationCenter, NgClass, AvatarModule, MenuModule],
  templateUrl: './topbar.html',
  styleUrl: './topbar.scss',
})
export class Topbar {
  auth = inject(AuthService);
  private translate = inject(TranslateService);
  private router = inject(Router);
  private api = inject(ApiService);

  @Output() menuToggle = new EventEmitter<void>();

  searchQuery = signal('');
  searchResults = signal<SearchResult[]>([]);
  searchOpen = signal(false);

  // Reactive current-route segment → translation key
  private routeKey = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map(e => this.resolveTitle(e.urlAfterRedirects)),
    ),
    { initialValue: this.resolveTitle(this.router.url) }
  );

  private resolveTitle(url: string): string {
    const segments = url.split('/').filter(s => s && !s.startsWith('?'));
    for (const seg of segments) {
      if (seg in ROUTE_TITLE_MAP) return ROUTE_TITLE_MAP[seg];
      const compound = segments.slice(0, segments.indexOf(seg) + 1).join('/');
      if (compound in ROUTE_TITLE_MAP) return ROUTE_TITLE_MAP[compound];
    }
    return 'nav.dashboard';
  }

  pageTitleKey = computed(() => this.routeKey() ?? 'nav.dashboard');

  languages: LangOption[] = [
    { code: 'en', label: 'English',    flag: 'us', dir: 'ltr' },
    { code: 'es', label: 'Español',    flag: 'es', dir: 'ltr' },
    { code: 'ar', label: 'العربية',    flag: 'sa', dir: 'rtl' },
    { code: 'fr', label: 'Français',   flag: 'fr', dir: 'ltr' },
    { code: 'de', label: 'Deutsch',    flag: 'de', dir: 'ltr' },
    { code: 'pt', label: 'Português',  flag: 'br', dir: 'ltr' },
    { code: 'tr', label: 'Türkçe',     flag: 'tr', dir: 'ltr' },
    { code: 'ru', label: 'Русский',    flag: 'ru', dir: 'ltr' },
  ];

  // First name + avatar shown in the header; falls back to initials when no photo.
  avatarImage = computed(() => this.auth.currentUser()?.avatar ?? null);
  avatarInitials = computed(() => {
    const u = this.auth.currentUser();
    const initials = ((u?.firstName?.[0] ?? '') + (u?.lastName?.[0] ?? '')).toUpperCase();
    return initials || 'A';
  });

  userMenuItems = signal<MenuItem[]>([]);

  selectedLang = signal<LangOption>(
    this.languages.find(l => l.code === (this.translate.currentLang || environment.defaultLang))
    ?? this.languages[0]
  );

  onLangChange(lang: LangOption): void {
    if (!lang) return;
    this.selectedLang.set(lang);
    this.translate.use(lang.code);
  }

  private searchResults$ = toObservable(this.searchQuery).pipe(
    debounceTime(250),
    switchMap(query => {
      if (!query || query.length < 2) {
        this.searchResults.set([]);
        this.searchOpen.set(false);
        return of(null);
      }
      this.searchOpen.set(true);
      this.searchResults.set([]);

      const requests = SEARCH_SOURCES.map(src =>
        this.api.getPage<any>(src.apiPath, { page: 0, size: 3 }, { search: query }).pipe(
          map(res => (res.content ?? []).map((item: any) => ({
            type: src.type,
            id: item.id,
            icon: src.icon,
            ...src.toResult(item),
          } as SearchResult))),
          catchError(() => of([] as SearchResult[])),
        )
      );
      return forkJoin(requests);
    }),
  );

  private searchResultsSignal = toSignal(this.searchResults$);

  constructor() {
    effect(() => {
      const resultArrays = this.searchResultsSignal();
      if (resultArrays) {
        this.searchResults.set(resultArrays.flat());
      }
    });
    this.buildUserMenu();
    this.translate.onLangChange.subscribe(() => this.buildUserMenu());
  }

  private buildUserMenu(): void {
    this.userMenuItems.set([
      {
        label: this.translate.instant('profile.myProfile'),
        icon: 'pi pi-user',
        command: () => this.router.navigate(['/settings/profile']),
      },
      {
        label: this.translate.instant('sessions.devices'),
        icon: 'pi pi-desktop',
        command: () => this.router.navigate(['/settings/devices']),
      },
      { separator: true },
      {
        label: this.translate.instant('common.logout'),
        icon: 'pi pi-sign-out',
        command: () => this.onLogout(),
      },
    ]);
  }

  onSearch(query: string): void {
    this.searchQuery.set(query);
  }

  goToResult(result: SearchResult): void {
    this.router.navigate([result.route]);
    this.searchOpen.set(false);
    this.searchQuery.set('');
    this.searchResults.set([]);
  }

  onLogout(): void {
    this.auth.logout().subscribe({ error: () => undefined });
  }
}
